import React, {
  lazy, Suspense, useContext, useState, useEffect, MouseEvent,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Button, Typography, Switch, Tooltip } from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CreateSpeedDial from 'components/CreateSpeedDial';
import PersonService from 'services/person';
import { LOGOS, SHOW_MODAL } from 'shared/constants';
import NationalService from 'services/national';
import {
  Case,
  CaseState,
  EmployerAdminAssignment, FamilyRelationship, ForeignNational, PersonType, Role,
  VisaStatus,
} from 'API';
import { UserContext } from 'shared/contexts/UserContext';
import { ROUTES } from 'shared/routes';
import { FIRM_COMPANY } from 'services/firm';
import { checkAdminAndPrimaryContactByCompany, checkSectionsCompleted, isRelativeAdmin } from 'shared/utilities';
import { MH_ROLES } from 'services/auth';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import S3Avatar from './S3Avatar';
import HeaderMobile from './HeaderMobile';
import HeaderMenu from './HeaderMenu';
import NotificationIcon from './NotificationIcon';
import HeaderTitle from './HeaderTitle';
import CustomSnackbar from './CustomSnackbar';

// TODO: We will remove this code in future because now we are using new component with Relative FN
// const OnboardingModalNational = lazy(
//   () => import('components/modals/OnboardingModalNational'),
// );

const OnboardingModalCompany = lazy(
  () => import('components/modals/OnboardingModalCompany'),
);

const OnboardingModalEmployer = lazy(
  () => import('components/modals/OnboardingModalEmployer'),
);

const NewsletterModal = lazy(
  () => import('components/modals/NewsletterModal'),
);

const OnboardingModalForeignNational = lazy(
  () => import('components/modals/OnboardingModalForeignNationals'),
);

const Header: React.FC = () => {
  const [userState, userDispatch] = useContext(UserContext);
  const { role, person, isUserVisibilityToggleOn } = userState;
  const {
    avatar, firstname, hasCompletedOnboardingQuestions, newsletterModalViewCount,
  } = person;
  const isEmployer = role === Role.EMPLOYER;
  const isNational = role === Role.FOREIGNNATIONAL;
  const isMH = MH_ROLES.includes(role);
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const showModel = Boolean(localStorage.getItem(SHOW_MODAL));
  const adminArray: EmployerAdminAssignment[] = [];
  const [checkSection, setCheckSection] = useState<boolean>(false);

  const isFilled = person.type === PersonType.Employer
    && !hasCompletedOnboardingQuestions;

  const [openOnboardEmployer, setOpenOnboardEmployer] = useState(
    isFilled,
  );

  const [openNewsletterModal, setOpenNewsletterModal] = useState(
    false,
  );

  const fnHasGlobalProcess = isNational && (person?.cases?.items?.some(
    (caseItem: Case) => caseItem.state === CaseState.OPEN
      && caseItem.requestedProcess === VisaStatus.GLOBAL_PROCESS,
  ) ?? false);

  const [openOnboardCompany, setOpenOnboardCompany] = useState(
    person.type === PersonType.Employer && person?.isPrimaryContact
    && checkAdminAndPrimaryContactByCompany(person).length && showModel,
  );

  const [openOnboardingFN, setOpenOnboardingFN] = useState<boolean>(
    person.type === PersonType.ForeignNational && checkSection,
  );

  const loadRelatives = async (isRelative: Array<FamilyRelationship | null>) => {
    const relativeArray: ForeignNational[] = [];

    try {
      await Promise.all(
        isRelative?.map(async (relative: FamilyRelationship | null) => {
          const getFN = await NationalService.getForeignNational(relative?.target?.id || '') as ForeignNational;

          const { onboardingSections } = getFN;

          const checkCompleted = checkSectionsCompleted(onboardingSections || { __typename: 'NationalOnboardingSections' });

          if (getFN && !checkCompleted) {
            relativeArray.push(getFN as ForeignNational);
          }

          return getFN;
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error >>>>>>', error);
    }

    return relativeArray;
  };

  const hasCompletedOnboardingAll = async (
    user: ForeignNational,
    // eslint-disable-next-line consistent-return
  ) => {
    if (user) {
      const isRelative = isRelativeAdmin(user);

      const checkSelf = checkSectionsCompleted(user.onboardingSections || { __typename: 'NationalOnboardingSections' });
      let relatives: ForeignNational[] = [];

      if (isRelative && isRelative?.length > 0) {
        relatives = await loadRelatives(isRelative);
      }

      if (!checkSelf || relatives.length) {
        setCheckSection(true);
      }
    }
  };

  useEffect(() => {
    hasCompletedOnboardingAll(person);
  }, []);

  useEffect(() => {
    if (checkSection) {
      setOpenOnboardingFN(true);
    }
  }, [checkSection]);

  useEffect(() => {
    if (hasCompletedOnboardingQuestions) {
      if (newsletterModalViewCount != null && newsletterModalViewCount < 3) {
        setOpenNewsletterModal(true);
      }
    }
  }, [hasCompletedOnboardingQuestions]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  person?.adminCompanies?.items?.filter((item: EmployerAdminAssignment) => item?.company?.primaryContacts?.items
    ?.filter((primary: any) => {
      if (primary?.employerID === item?.employerID && !item.company?.hasCompletedOnboardingQuestions) {
        adminArray.push(item);
      }

      return item;
    }));

  return (
    <>
      <Box
        component="header"
        sx={(t) => ({
          display: { xs: 'none', md: 'grid' },
          alignItems: 'center',
          backgroundColor: t.palette.backgroundPrimary,
          borderBottom: `1px solid ${t.palette.divider}`,
          borderLeft: 'none',
          gridTemplateColumns: isEmployer ? '1fr auto auto auto auto' : '1fr auto auto auto',
          gridColumn: 'header-start / header-end',
          gridRow: 'header-start / header-end',
          padding: '1rem',
          zIndex: 20,
          [t.breakpoints.down('lg')]: {
            gridTemplateColumns: isEmployer
              ? 'auto 1fr auto auto auto auto'
              : 'auto 1fr auto auto auto',
            columnGap: '1rem',
          },
        })}
      >

        {!fnHasGlobalProcess && openOnboardingFN && (
          <Suspense fallback={null}>
            <OnboardingModalForeignNational
              open={openOnboardingFN}
              setOpen={setOpenOnboardingFN}
            />
          </Suspense>
        )}

        {openOnboardEmployer && (
          <Suspense fallback={null}>
            <OnboardingModalEmployer
              open={openOnboardEmployer}
              setOpen={setOpenOnboardEmployer}
              disableClose
            />
          </Suspense>
        )}

        {openNewsletterModal && (
          <Suspense fallback={null}>
            <NewsletterModal
              open={openNewsletterModal}
              setOpen={setOpenNewsletterModal}
            />
          </Suspense>
        )}

        {!!openOnboardCompany && !openOnboardEmployer && adminArray.length > 0 && (
          <Suspense fallback={null}>
            <OnboardingModalCompany
              open={openOnboardCompany}
              setOpen={setOpenOnboardCompany}
            />
          </Suspense>
        )}

        {/*
        // TODO: We will remove this code in future because now we are using new component with Relative FN
        {relativeAdmin.length === 0 && openOnboardNational && (
          <Suspense fallback={null}>
            <OnboardingModalNational
              open={openOnboardNational}
              setOpen={setOpenOnboardNational}
              user={person}
            />
          </Suspense>
        ) */}

        <Box
          component={Link}
          to={ROUTES.home}
          sx={{ display: { xs: 'block', lg: 'none' }, height: '4rem', width: '4rem' }}
        >
          <Box
            component="img"
            src={LOGOS.primary}
            alt={`${FIRM_COMPANY.name} logo`}
            height="64"
            width="64"
            sx={{ display: { xs: 'block', lg: 'none' }, height: '4rem', width: '4rem' }}
          />
        </Box>
        <Typography
          variant="h3"
          component="h1"
          sx={(t) => ({
            flexGrow: 1,
            fontSize: '2rem',
            fontWeight: t.typography.fontWeightBold as any,
            [t.breakpoints.down('lg')]: { fontSize: '1.5rem' },
          })}
        >
          <HeaderTitle />
        </Typography>

        <Box display="flex" sx={{ gap: '2rem' }}>
          {isEmployer && (
            <Button
              component={Link}
              startIcon={<AssignmentIndIcon />}
              to={ROUTES.processRequests}
              disabled={location.pathname === ROUTES.processRequests}
            >
              My Process Requests
            </Button>
          )}

          {isNational && (
            <Button
              component={Link}
              startIcon={<CloudUploadIcon />}
              to={`${ROUTES.people}/${person.slug}/documents`}
              disabled={location.pathname === ROUTES.processRequests}
            >
              Upload Documents
            </Button>
          )}

          {isMH && (
            <Box display="flex" alignItems="center" justifyContent="center">
              <Tooltip
                title={(
                  <Box sx={{ margin: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ color: 'common.white', fontSize: '0.75rem' }}>FOREIGN NATIONAL</Box>
                      <Box
                        component="span"
                        sx={(t) => ({
                          width: t.spacing(1.5),
                          height: t.spacing(1.5),
                          borderRadius: '50%',
                          marginLeft: t.spacing(1),
                          backgroundColor: '#DA7941',
                        })}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
                      <Box component="span" sx={{ color: 'common.white', fontSize: '0.75rem' }}>EMPLOYER</Box>
                      <Box
                        component="span"
                        sx={(t) => ({
                          width: t.spacing(1.5),
                          height: t.spacing(1.5),
                          borderRadius: '50%',
                          marginLeft: t.spacing(1),
                          backgroundColor: '#546587',
                        })}
                      />
                    </Box>
                  </Box>
                )}
                arrow
              >
                <Switch
                  sx={{ marginTop: '5px', marginRight: '20px' }}
                  checked={isUserVisibilityToggleOn}
                  onChange={(event) => userDispatch({ type: 'set visibility toggle state', isUserVisibilityToggleOn: event.target.checked })}
                  name="headerToggle"
                  color="primary"
                />
              </Tooltip>
              <Button
                sx={(t) => ({
                  height: '2.5rem',
                  color: 'black',
                  [t.breakpoints.down('lg')]: { height: '40px' },
                })}
                component={Link}
                startIcon={<TaskAltIcon sx={{ pointerEvents: 'none' }} />}
                to={`${ROUTES.tasks}?mine=true`}
                disabled={location.pathname === ROUTES.tasks}
              >
                My Tasks
              </Button>
            </Box>
          )}

          <NotificationIcon />

          <CreateSpeedDial />

          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
            onClick={handleClick}
          >
            <Typography
              sx={{ fontSize: '0.875rem', fontStyle: 'uppercase', fontWeight: 500, marginRight: '1rem' }}
              component="h2"
              variant="h5"
            >
              {firstname}
            </Typography>
            <S3Avatar
              alt={PersonService.fullname(person)}
              sx={(t) => ({
                height: t.spacing(6),
                width: t.spacing(6),
                '&:hover': { cursor: 'pointer' },
              })}
              picture={avatar}
              shadow
              id="header-avatar"
            />
          </Box>
          <HeaderMenu
            open={open}
            handleClose={handleClose}
            anchorEl={anchorEl}
          />
        </Box>

        <HeaderMobile
          sx={{ display: { xs: 'grid', md: 'none' } }}
        />
        <CustomSnackbar />
      </Box>
    </>
  );
};

export default Header;
